import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './ProductsPage.css';

const ProductsPage = () => {
  // Define the product data using useMemo to prevent re-creation on each render
  const products = useMemo(() => [
    {
      id: 1,
      name: "Electroplated Diamond & CBN Tools",
      image: "ed_cbn_tool.jpg",
      technologyOverview: "Electroplated Diamond & CBN tools feature a single layer of abrasive grains directly bonded to the tool surface through electroplating. This process allows the grains to protrude prominently, providing a sharp, aggressive cutting edge that remains effective throughout the tool's life. Electroplated tools are ideal for applications that require high precision, such as grinding, drilling, and finishing, particularly on materials that are difficult to machine. The electroplated structure ensures minimal heat generation and optimal chip clearance, enhancing cutting efficiency in both wet and dry conditions.",
      advantagesApplications: "Electroplated tools offer unmatched sharpness, reduced heat buildup, and exceptional cutting performance, making them highly suitable for aerospace components, automotive parts, and electronic circuit boards. The single-layer abrasive design allows for cool cutting and quick material removal, improving overall productivity. These tools can be re-plated, providing a cost-effective solution that extends their operational lifespan, making them popular in industries requiring precise, high-speed material removal.",
      variants: ["Files", "Internal Grinding wheel/pin", "Parting/cutoff wheel-cutter", "Hole saw"]
    },
    {
      id: 2,
      name: "Metal/Resin Bonded Diamond & CBN Tools",
      image: "metal_resin.JPG",
      technologyOverview: "Metal and Resin Bonded Diamond & CBN tools consist of multiple layers of abrasive particles embedded within a bonding matrix. Metal bonds are ideal for applications that demand extreme durability and thermal stability, often used in high-load, high-heat environments. Resin bonds, softer and more flexible, offer excellent stock removal rates with a smoother surface finish, making them ideal for fine grinding and polishing. The multi-layer construction allows these tools to maintain abrasive performance over extended use, even under heavy-duty conditions.",
      advantagesApplications: "These bonded tools are essential in industries like ceramics, stone processing, and advanced material machining, where consistency and durability are crucial. Metal bonds are preferred for cutting and grinding harder materials, such as glass, silicon carbide, and advanced composites, due to their heat resistance. Resin bonds excel in polishing and finishing applications, delivering fine surface quality. Their long-lasting nature reduces tool changes and downtime, enhancing operational efficiency in demanding industrial settings.",
      variants: ["Internal Grinding wheel/pin", "Parting/cutoff wheel-cutter", "Cup wheel", "Saucer Wheel"]
    },
    {
      id: 3,
      name: "Tungsten Carbide Dies & Wear Parts",
      image: "tungsten-carbide.JPG",
      technologyOverview: "Tungsten Carbide Dies and Wear Parts are engineered using high-grade carbide material, renowned for its exceptional hardness and resistance to abrasion. These components are meticulously manufactured to withstand the rigors of metal forming, drawing, and extrusion processes. The high compressive strength of tungsten carbide ensures that these dies maintain their shape and dimensional accuracy, even under intense pressures, making them indispensable for precision applications.",
      advantagesApplications: "Carbide dies are crucial in industries like metal forming, welding rod manufacturing, and automotive for shaping and drawing processes. Their robust design minimizes wear and prolongs service life, reducing maintenance costs and downtime. Tungsten carbide's ability to resist wear and deformation under high-stress conditions ensures consistent product quality, improving productivity in high-volume manufacturing environments.",
      variants: ["TC Bits", "Wire Drawing dies", "Drills", "Collapsible tube die ring", "TC Inserted Nozel/Wire Guide", "TC Inserted extruder / full Tc extruder"]
    },
    {
      id: 4,
      name: "Diamond Paste & Micron Powder/Boron Carbide",
      image: "diamond-paste.JPG",
      technologyOverview: "Diamond Paste and Micron Powder are produced using tightly controlled manufacturing processes to achieve uniform particle distribution, ensuring consistent abrasive action for precision polishing and lapping. Boron Carbide, one of the hardest materials available, is utilized for its exceptional abrasive properties, offering a reliable solution for critical finishing processes. These materials are available in various grit sizes, catering to a wide range of polishing requirements, from coarse to ultra-fine.",
      advantagesApplications: "Diamond pastes and powders are extensively used in optics, precision tool manufacturing, and high-tech engineering for obtaining the finest surface finishes. They deliver superior material removal rates and are ideal for applications demanding tight tolerances. Boron carbide's extreme hardness makes it suitable for abrasive applications in lapping and polishing advanced materials, such as ceramics and carbide tools, providing excellent results in demanding environments.",
      variants: ["Diamond Paste D1 to D100 Micron", "Diamond Powder 0¼ to 60 Grade", "Boron Carbide Powder and Paste 80 to 400 Mesh"]
    },
    {
      id: 5,
      name: "Diamond Dressers & PCD/PCBN Tools",
      image: "diamond-dressers.JPG",
      technologyOverview: "Diamond Dressers and PCD/PCBN tools are critical for maintaining grinding wheel profiles and executing precision cuts on hard materials. Manufactured from high-quality synthetic and natural diamonds, as well as polycrystalline diamond (PCD) and cubic boron nitride (PCBN), these tools provide unmatched wear resistance and cutting efficiency. Available in various configurations, these dressers and tools are essential for industries requiring the maintenance of sharp, accurate grinding and cutting surfaces.",
      advantagesApplications: "Used in automotive, aerospace, and precision tool manufacturing, these tools significantly enhance grinding efficiency and cutting speeds. PCD/PCBN tools offer superior performance compared to traditional carbide tools, providing faster material removal rates, improved cycle times, and a high-quality surface finish. Their ability to maintain dimensional accuracy and consistency across production runs makes them invaluable in high-precision applications.",
      variants: ["Single Point 0.25 to 4.0 ct", "Multi Point Dg 10 & 12"]
    },
    {
      id: 6,
      name: "Vacuum Brazed Tools",
      image: "vaccum-brazed.JPG",
      technologyOverview: "Vacuum Brazed Tools utilize a unique bonding method where diamond or CBN particles are directly brazed onto the tool's surface, forming a robust cutting edge. This brazing process maximizes the exposure of abrasive particles, enhancing cutting efficiency and tool longevity. Vacuum brazed tools are designed to tackle the toughest materials, providing sharp, aggressive cuts with minimal effort.",
      advantagesApplications: "Ideal for heavy-duty applications such as construction, mining, and stone processing, these tools deliver exceptional cutting speed and durability. They are highly effective in aggressive cutting tasks, including concrete, granite, and metal alloy processing. The robust nature of vacuum brazed tools ensures prolonged use in harsh environments, making them indispensable for industries where tool longevity and performance are paramount.",
      variants: ["Vacuum Brazed Engraving Tools", "Sintered Engraving Tools"]
    }
  ], []);

  // State for selected product
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchParams] = useSearchParams();

  // Check if a product ID is in the URL and auto-select it
  useEffect(() => {
    const productId = searchParams.get('id');
    if (productId) {
      const product = products.find(p => p.id === parseInt(productId));
      if (product) {
        setSelectedProduct(product);
      }
    }
  }, [searchParams, products]);

  return (
    <div className="products-page">
      <section className="product-page-header">
        {/* <div className="page-header-overlay"></div> */}
        <div className="page-header-container">
          <div className="container">
            <h1 className="page-title">
              Our <span className="highlight">Products</span>
            </h1>
            <p className="page-subtitle">
              Explore our comprehensive range of high-quality industrial tools and solutions
            </p>
          </div>
        </div>
      </section>

      <div className="container">
        {/* Grid View */}
        {!selectedProduct && (
          <div className="products-grid">
            {products.map(product => (
              <div 
                key={product.id} 
                className="product-card"
                onClick={() => setSelectedProduct(product)}
              >
                <div className="product-image-container">
                  <img 
                    src={`/images/products/${product.image}`}
                    alt={product.name}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
                <h3 className="product-name">{product.name}</h3>
              </div>
            ))}
          </div>
        )}

        {/* Detail View */}
        {selectedProduct && (
          <div className="product-detail-view">
            <button 
              className="back-button"
              onClick={() => setSelectedProduct(null)}
            >
              ← Back to Products
            </button>
            
            <div className="product-detail-content">
              <div className="detail-image">
                <img 
                  src={`/images/products/${selectedProduct.image}`}
                  alt={selectedProduct.name}
                />
              </div>
              
              <div className="detail-info">
                <h2 className="detail-title">{selectedProduct.name}</h2>
                
                <div className="detail-section">
                  <h4>Technology Overview</h4>
                  <p>{selectedProduct.technologyOverview}</p>
                </div>
                
                <div className="detail-section">
                  <h4>Advantages & Applications</h4>
                  <p>{selectedProduct.advantagesApplications}</p>
                </div>
                
                <div className="detail-section">
                  <h4>Available Products</h4>
                  <ul className="variants-list">
                    {selectedProduct.variants.map((variant, index) => (
                      <li key={index}>{variant}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;
